import React, { useMemo, useState, useEffect } from 'react';
import { Layout, Menu, Button, Avatar, Typography, Tag } from 'antd';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  HomeOutlined,
  TeamOutlined,
  DollarOutlined,
  SettingOutlined,
  LogoutOutlined,
  BarChartOutlined,
  FileOutlined,
} from '@ant-design/icons';
import './SidebarStyles.css';

const { Sider } = Layout;
const { Text } = Typography;

const WhiteLabelAgencySidebar = ({ collapsed, onCollapse }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout, currentUser } = useAuth();

  // Display name and avatar logic
  const displayName = useMemo(() => {
    const u = currentUser || {};
    return (
      u.display_name ||
      u.displayName ||
      (u.first_name && u.last_name ? `${u.first_name} ${u.last_name}` : null) ||
      u.name ||
      u.email?.split('@')[0] ||
      'User'
    );
  }, [currentUser]);

  const computedAvatar = useMemo(() => {
    const u = currentUser || {};
    return u.profile_picture_url || u.photoURL || u.avatar_url || u.image_url || null;
  }, [currentUser]);

  const [avatarUrl, setAvatarUrl] = useState(null);
  useEffect(() => {
    if (computedAvatar && /^\//.test(computedAvatar)) {
      setAvatarUrl(window.location.origin + computedAvatar);
    } else {
      setAvatarUrl(computedAvatar);
    }
  }, [computedAvatar]);

  const menuItems = useMemo(() => ([
    { key: '/whitelabel/dashboard', icon: <HomeOutlined />, label: 'Dashboard', path: '/whitelabel/dashboard' },
    { key: '/whitelabel/clients', icon: <TeamOutlined />, label: 'Clients', path: '/whitelabel/clients' },
    { key: '/whitelabel/campaigns', icon: <BarChartOutlined />, label: 'Campaigns', path: '/whitelabel/campaigns' },
    { key: '/whitelabel/commissions', icon: <DollarOutlined />, label: 'Commissions', path: '/whitelabel/commissions' },
    { key: '/whitelabel/settings', icon: <SettingOutlined />, label: 'Settings', path: '/whitelabel/settings' },
  ]), []);

  // Flatten items so that in collapsed mode all items render as icons with tooltips
  const flattenedItems = useMemo(() => {
    const flat = [];
    const walk = (items, parentIcon = null) => {
      items.forEach((it) => {
        const base = {
          key: it.key,
          icon: it.icon || parentIcon || <FileOutlined />,
          label: it.label,
          title: typeof it.label === 'string' ? it.label : undefined,
          path: it.path
        };
        if (it.children && it.children.length) {
          if (it.path) flat.push(base);
          walk(it.children, it.icon || parentIcon);
        } else {
          flat.push(base);
        }
      });
    };
    walk(menuItems);
    return flat.map(({ key, icon, label, title }) => ({ key, icon, label, title }));
  }, [menuItems]);

  const onMenuClick = ({ key }) => {
    // Keys are defined as full paths for both top-level and submenu items
    if (typeof key === 'string') {
      navigate(key);
    }
  };

  const handleLogout = () => {
    // Prefer header logout, but keep a footer logout for consistency
    logout();
    navigate('/login');
  };

  return (
    <Sider
      collapsible
      collapsed={collapsed}
      onCollapse={onCollapse}
      className="sidebar-container"
      width={250}
      collapsedWidth={80}
      trigger={null}
    >
      <div className="sidebar-header">
        <div className="logo-container" style={{ 
          padding: collapsed ? '12px 8px' : '16px', 
          textAlign: 'center',
          borderBottom: '1px solid rgba(0, 0, 0, 0.06)'
        }}>
          <img 
            src="/SidebarLogo.png" 
            alt="PathMarkr Logo" 
            style={{ 
              height: 'auto',
              maxHeight: collapsed ? '40px' : '50px', 
              maxWidth: '100%', 
              width: 'auto',
              objectFit: 'contain',
              cursor: 'pointer',
              transition: 'all 0.3s ease'
            }}
            onClick={() => onCollapse(!collapsed)}
          />
          {!collapsed && (
            <div style={{ marginTop: '8px' }}>
              <Text strong style={{ fontSize: '14px', color: '#1f2937' }}>
                Reseller Portal
              </Text>
            </div>
          )}
        </div>
      </div>

      {!collapsed && (
        <div
          style={{
            padding: '24px 16px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            borderBottom: '1px solid rgba(0, 0, 0, 0.06)'
          }}
        >
          {avatarUrl ? (
            <img
              src={avatarUrl}
              alt="avatar"
              onError={() => { setAvatarUrl(null); }}
              style={{ width: 80, height: 80, borderRadius: '50%', objectFit: 'cover', border: '1px solid #f0f0f0', marginBottom: 8 }}
            />
          ) : (
            <Avatar
              size={80}
              style={{
                backgroundColor: 'var(--brand-blue)',
                fontSize: '32px',
                fontWeight: 'bold',
                marginBottom: 8
              }}
            >
              {displayName
                .split(' ')
                .map((n) => n && n[0])
                .filter(Boolean)
                .join('')
                .toUpperCase()}
            </Avatar>
          )}
          <div style={{ marginTop: '8px' }}>
            <Text strong style={{ display: 'block', color: 'var(--black)' }}>
              {displayName}
            </Text>
            {currentUser?.email && (
              <Text style={{ fontSize: '12px', color: 'var(--muted-gray)', display: 'block' }}>{currentUser.email}</Text>
            )}
            <Tag color="purple" style={{ marginTop: '8px', fontSize: '11px' }}>
              RESELLER
            </Tag>
          </div>
        </div>
      )}

      <div className="sidebar-menu-wrapper">
        <Menu
          mode="inline"
          theme="light"
          inlineCollapsed={collapsed}
          selectedKeys={[location.pathname]}
          onClick={onMenuClick}
          items={collapsed ? flattenedItems : menuItems}
          className="sidebar-menu"
          style={{
            borderRight: 0,
            background: 'transparent',
            marginTop: '16px'
          }}
        />
      </div>

      <div className="sidebar-footer" style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        padding: '8px 12px',
        borderTop: '1px solid rgba(0, 0, 0, 0.06)',
        background: 'transparent'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
          <Button
            type="text"
            className="sidebar-action sidebar-action--danger"
            icon={<LogoutOutlined />}
            onClick={handleLogout}
            danger
          >
            {!collapsed && 'Logout'}
          </Button>
        </div>
        {!collapsed && (
          <div style={{ textAlign: 'center', fontSize: '11px', color: 'var(--muted-gray)', whiteSpace: 'nowrap' }}>
            v1.4.7 Alpha | © 2026 PathMarkr
          </div>
        )}
      </div>
    </Sider>
  );
};

export default WhiteLabelAgencySidebar;
